/**
 * Settings (section 2): Team (who works on this brand), Advanced (owners only: the default
 * language, importing a whole catalogue, a preview link for test builds). Brand, History and Files
 * have their own modules.
 */

import { useCallback, useEffect, useState } from "react";
import Button from "@web-ui/components/Button";
import Chip from "@web-ui/components/Chip";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import Select from "@web-ui/components/Select";
import { people, publishing, type Problem, type Role, type Staff } from "@/api/admin";
import { ApiError } from "@/api/client";
import { dateTime } from "@/lib/format";
import { useStudio } from "@/shell/studio";

const ROLES: Role[] = ["owner", "editor", "viewer"];
const ROLE_WORDS: Record<Role, string> = { owner: "Owner", editor: "Editor", viewer: "Viewer" };

export function Team() {
  const { client, staff: me } = useStudio();
  const [rows, setRows] = useState<Array<Staff & { createdAt: number }>>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("editor");
  const [message, setMessage] = useState("");
  const owner = me.role === "owner";
  const load = useCallback(() => people.staff(client).then((r) => setRows(r.staff)), [client]);
  useEffect(() => { void load().catch(() => setMessage(owner ? "The team did not load." : "")); }, [load, owner]);
  const explain = (e: unknown) => (e instanceof ApiError ? ({ last_owner: "There must always be at least one owner.", invalid_email: "That is not an email address." } as Record<string, string>)[e.code] ?? "That did not go through." : "That did not go through.");
  const set = async (address: string, r: Role) => { try { await people.setStaff(client, address, r); setMessage(""); setEmail(""); await load(); } catch (e) { setMessage(explain(e)); } };
  const remove = async (s: Staff) => { if (!window.confirm(`Remove ${s.email} from the ${client.brand.name} team? They can no longer sign in to this console.`)) return; try { await people.removeStaff(client, s.accountId); await load(); } catch (e) { setMessage(explain(e)); } };
  const roleOptions = ROLES.map((r, i) => ({ id: i, name: ROLE_WORDS[r] }));
  return (
    <>
      {message ? <Notice tone="critical" onDismiss={() => setMessage("")}>{message}</Notice> : null}
      {owner ? (
        <section className="card flex flex-col gap-3">
          <h2 className="text-sub-title-1 ps-card-inset">Add someone</h2>
          <p className="text-body-2 text-t-secondary ps-card-inset">Editors change things and publish; viewers only look; owners also manage the team and the brand. They sign in with this address.</p>
          <div className="grid gap-2 md:grid-cols-[1fr_12rem_auto] md:items-end">
            <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Select label="Role" value={roleOptions[ROLES.indexOf(role)]!} options={roleOptions} onChange={(o) => setRole(ROLES[o.id]!)} />
            <Button variant="primary" type="button" disabled={!email.includes("@")} onClick={() => void set(email, role)}>Add to the team</Button>
          </div>
        </section>
      ) : null}
      <section className="card">
        {owner ? (
          <DataTable label="Team" cellsThead={<><th>Email</th><th>Role</th><th>Since</th><th /></>}>
            {rows.map((s) => (
              <DataTableRow key={s.accountId}>
                <td className="text-body-2">{s.email}{s.accountId === me.accountId ? <span className="text-t-tertiary"> (you)</span> : null}</td>
                <td><div className="w-36"><Select ariaLabel={`Role of ${s.email}`} value={roleOptions[ROLES.indexOf(s.role)]!} options={roleOptions} onChange={(o) => void set(s.email, ROLES[o.id]!)} /></div></td>
                <td className="text-body-2 text-t-secondary">{dateTime(s.createdAt)}</td>
                <td><Button type="button" size="xs" variant="danger-soft" onClick={() => void remove(s)}>Remove</Button></td>
              </DataTableRow>
            ))}
          </DataTable>
        ) : (
          <div className="flex flex-col gap-2 p-3">
            <p className="text-body-2">You are {ROLE_WORDS[me.role].toLowerCase() === "editor" ? "an editor" : "a viewer"} on {client.brand.name} <Chip size="sm">{ROLE_WORDS[me.role]}</Chip></p>
            <p className="text-body-2 text-t-secondary">Owners manage the team. Ask one to add or remove people.</p>
          </div>
        )}
      </section>
    </>
  );
}

export function Advanced() {
  const { client, isOwner } = useStudio();
  const [locale, setLocale] = useState("");
  const [feedText, setFeedText] = useState("");
  const [result, setResult] = useState<{ tone: "positive" | "critical"; text: string; problems?: Problem[] } | null>(null);
  const [preview, setPreview] = useState<{ token: string; expiresAt: number } | null>(null);
  useEffect(() => { void publishing.settings(client).then((s) => setLocale(s.defaultLocale)).catch(() => undefined); }, [client]);
  const options = client.brand.locales.supported.map((l, i) => ({ id: i, name: l }));
  const previewUrl = preview ? `${client.brand.apiBase}/v1/feed?preview=${preview.token}` : "";

  const importFeed = async () => {
    try {
      const out = await publishing.importFeed(client, JSON.parse(feedText));
      setResult({ tone: "positive", text: `Imported: ${out.created} new, ${out.updated} updated. They show in the app after you review and publish.` }); setFeedText("");
    } catch (e) {
      if (e instanceof SyntaxError) setResult({ tone: "critical", text: "That is not a catalogue file (it isn't valid JSON)." });
      else if (e instanceof ApiError && e.code === "feed_invalid") setResult({ tone: "critical", text: "Not imported: the catalogue has problems.", problems: e.body["problems"] as Problem[] });
      else setResult({ tone: "critical", text: "The import did not go through." });
    }
  };

  if (!isOwner) return <Notice tone="info">Only owners can change these settings.</Notice>;
  return (
    <>
      <section className="card flex flex-col gap-3">
        <h2 className="text-sub-title-1 ps-card-inset">Default language</h2>
        <div className="max-w-xs">
          <Select label="Language" value={options.find((o) => o.name === locale) ?? null} options={options} onChange={(o) => void publishing.saveSettings(client, o.name).then((s) => setLocale(s.defaultLocale))} />
        </div>
        <p className="text-caption text-t-secondary ps-card-inset">Every name needs this language; the apps fall back to it.</p>
      </section>
      <section className="card flex flex-col gap-3">
        <h2 className="text-sub-title-1 ps-card-inset">Import a whole catalogue</h2>
        <p className="text-body-2 text-t-secondary ps-card-inset">Paste a catalogue file (ViewForm's format). Its items are added or updated here and show in the app after you review and publish.</p>
        {result ? (
          <Notice tone={result.tone} onDismiss={() => setResult(null)}>
            {result.text}
            {result.problems ? <ul className="mt-2 list-disc ps-5">{result.problems.slice(0, 20).map((p, i) => <li key={i}>{p.itemId ? <b>{p.itemId}: </b> : null}{p.message}</li>)}</ul> : null}
          </Notice>
        ) : null}
        <textarea aria-label="Catalogue file" className="min-h-64 w-full rounded-2xl bg-b-surface1 p-4 font-mono text-caption" spellCheck={false} value={feedText} onChange={(e) => setFeedText(e.target.value)} />
        <div><Button variant="secondary" type="button" disabled={!feedText.trim()} onClick={() => void importFeed()}>Import</Button></div>
      </section>
      <section className="card flex flex-col gap-3">
        <h2 className="text-sub-title-1 ps-card-inset">Preview link for test builds</h2>
        <p className="text-body-2 text-t-secondary ps-card-inset">Shows drafts in a test build of the app for two hours (launch it with <code>--feed draft:&lt;token&gt;</code>).</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => void publishing.previewToken(client).then(setPreview).catch(() => setResult({ tone: "critical", text: "No preview link: this brand has no identity secret." }))}>Make a preview link</Button>
          {preview ? <Button type="button" size="sm" variant="ghost" onClick={() => void navigator.clipboard.writeText(previewUrl)}>Copy link</Button> : null}
        </div>
        {preview ? <p className="break-all text-caption text-t-tertiary ps-card-inset">{previewUrl} · until {dateTime(preview.expiresAt)}</p> : null}
      </section>
    </>
  );
}
